import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Home, Trash2, PlusCircle, Search as SearchIcon, ShieldCheck, Star } from 'lucide-react';
import Header from '../components/Header';
import TradeCard from '../components/TradeCard';
import { useAuth } from '../context/AuthContext';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

export default function AgencyPortal() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState([]);
  const [form, setForm] = useState({ address: '', suburb: '', label: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data }, panelRes] = await Promise.all([api.listProperties(), api.preferredPanel()]);
      setItems(data);
      setPanel(panelRes.data);
    }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (user === false) return <Navigate to="/login" replace />;
  if (user && user.role !== 'agent') return <Navigate to="/" replace />;

  const isStrata = /strata/i.test(user?.company || '');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address.trim() || !form.suburb.trim()) return;
    setBusy(true);
    try {
      await api.createProperty({ address: form.address.trim(), suburb: form.suburb.trim(), label: form.label.trim() || null });
      setForm({ address: '', suburb: '', label: '' });
      toast.success('Property added');
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.deleteProperty(id); toast.success('Removed'); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="agency-page">
      <Header />
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">{isStrata ? 'Strata Portal' : 'Agency Portal'}</div>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A]">{user?.company || 'My properties'}</h1>
            <p className="mt-2 max-w-2xl text-[#5C5B5A]">
              {isStrata
                ? 'Manage the buildings you look after. See live compliance for every tradie before you engage them — no more chasing certificates before every scheduled maintenance.'
                : 'Manage the properties you look after. Search verified trades and see their live compliance status — no more chasing certificates of currency by email.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/search" className="btn-ghost" data-testid="agency-search-trades"><SearchIcon size={16} /> Find trades</Link>
          </div>
        </div>

        {/* Trust promise banner */}
        <div className="mt-6 flex items-center gap-4 border border-[#2A9D8F]/30 bg-[#2A9D8F]/[0.06] p-5" data-testid="agency-trust-banner">
          <ShieldCheck size={26} className="text-[#2A9D8F]" />
          <div className="text-sm text-[#1C1B1A]">
            Every listed tradie&apos;s license, insurance and workers-comp is tracked with expiry dates.
            Open any tradie profile to see their live compliance status before you engage them.
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-4" data-testid="add-property-form">
          <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Street address" data-testid="property-address" className="input-editorial md:col-span-2" />
          <input required value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })}
            placeholder="Suburb" data-testid="property-suburb" className="input-editorial" />
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Label (optional)" data-testid="property-label" className="input-editorial" />
          <button type="submit" disabled={busy} data-testid="add-property-btn" className="btn-primary justify-center md:col-span-4 md:w-fit">
            <PlusCircle size={16} /> {busy ? 'Adding...' : 'Add property'}
          </button>
        </form>

        <div className="mt-10 space-y-3" data-testid="properties-list">
          {items.length === 0 ? (
            <div className="border border-[#E5E4E0] bg-white p-8 text-center text-[#8A8988]">No properties yet.</div>
          ) : items.map((p) => (
            <div key={p.id} className="flex items-center justify-between border border-[#E5E4E0] bg-white p-5" data-testid={`property-${p.id}`}>
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center bg-[#F3F2EE] text-[#1A362D]"><Home size={20} /></div>
                <div>
                  {p.label && <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A8988]">{p.label}</div>}
                  <div className="font-heading text-lg font-bold text-[#1C1B1A]">{p.address}</div>
                  <div className="text-sm text-[#5C5B5A]">{p.suburb} · {p.saved_tradies.length} saved trades</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/search?suburb=${encodeURIComponent(p.suburb)}`} className="btn-ghost" data-testid={`find-trades-${p.id}`}>Find trades</Link>
                <button onClick={() => remove(p.id)} data-testid={`delete-property-${p.id}`} className="p-2 text-[#8A8988] hover:text-[#E76F51]">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Preferred Panel — the strata / agency killer feature */}
        <div className="mt-16 border-t border-[#E5E4E0] pt-10" data-testid="preferred-panel-section">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#1A362D]">Preferred Panel</div>
              <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#1C1B1A]">
                {isStrata ? 'Your approved vendor list' : 'Your go-to tradies'}
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-[#5C5B5A]">
                {isStrata
                  ? 'Every property manager in your office pulls from this list. Curate once, cover every building. Only verified tradies with current compliance appear here.'
                  : 'Save the tradies you actually trust. Fast dial for the next callout.'}
              </p>
            </div>
            <Link to="/search" className="btn-ghost text-sm" data-testid="panel-add-link">
              <PlusCircle size={14} /> Add tradies
            </Link>
          </div>

          <div className="mt-6 space-y-3" data-testid="panel-list">
            {panel.length === 0 ? (
              <div className="border border-dashed border-[#E5E4E0] bg-white p-8 text-center text-[#8A8988]" data-testid="panel-empty">
                <Star size={24} className="mx-auto text-[#E9C46A]" />
                <div className="mt-3 font-heading text-lg font-bold text-[#1C1B1A]">Panel is empty</div>
                <div className="mt-1 text-sm">Open any tradie&apos;s profile and tap &quot;Add to Preferred Panel&quot;.</div>
              </div>
            ) : panel.map((t) => <TradeCard key={t.id} trade={t} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
